import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Slider from "@mui/material/Slider";
import { useTypedSelector } from "hooks/store";
import { builder } from "Classes/global";
import { Robot } from "Classes/Robot";
import "./CMRSliderComponent.scss";
import { StaticMesh } from "Classes/StaticMesh";
import { AssetType } from "Classes/enums";

export const enum SliderType {
    Bsu = "bsu",
    Port = "port",
    Console = "console",
    Stack = "stack",
    Trolley = "trolley",
    Surgeon = "surgeon",
    Anesthesia = "anesthesia",
    Slave = "slave",
}
interface ComponentProps {
    type: SliderType;
    title: string;
    rotate?: string | null;
}

const defaultProps: ComponentProps = {
    type: SliderType.Bsu,
    title: "Bsu",
    rotate: "",
};
const CMRHeightSlider = styled(Slider)(() => ({
    color: "#E7E7E7",
    height: 6,
    padding: "15px 0",
    "& .MuiSlider-thumb": {
        backgroundColor: "#E6117B",
    },
    "& .MuiSlider-valueLabel": {
        fontSize: 12,
        fontWeight: "normal",
        top: -6,
        marginTop: 60,
        backgroundColor: "unset",
        color: "#E6117B",
        "&:before": {
            display: "none",
        },
        "& *": {
            background: "transparent",
            color: "#E6117B",
        },
    },
    "& .MuiSlider-track": {
        border: "none",
    },
}));

const CMRSliderComponent = ({ type, title, rotate }: ComponentProps): JSX.Element => {
    const [size, setSize] = useState<number | string | null>(0);
    const selectedBsu = useTypedSelector((store) => store.Scene.selectedBSU);
    const selectedPort = useTypedSelector((store) => store.Scene.selectedPort);
    const selectedConsole = useTypedSelector((store) => store.Scene.selectConsole);
    const selectedStack = useTypedSelector((store) => store.Scene.selectedStack);
    const selectedTrolley = useTypedSelector((store) => store.Scene.selectedTrolley);
    const selectedAnesthesia = useTypedSelector((store) => store.Scene.selectedAnesthesia);
    const selectedSurgeon = useTypedSelector((store) => store.Scene.selectedSurgeon);
    const [sliderMinValue, setSliderMinValue] = useState<number>(0);
    const [sliderMaxValue, setSliderMaxValue] = useState<number>(100);
    const [step, setStep] = useState<number>(1);

    useEffect(() => {
        switch (type) {
            case SliderType.Bsu:
                if (rotate === "Y") {
                    setSize(selectedBsu ? Math.floor(selectedBsu.currentRotation) : 0);
                    setStep(90);
                    setSliderMaxValue(180);
                    setSliderMinValue(-180);
                } else {
                    setStep(1);
                    setSize(selectedBsu ? Math.floor(selectedBsu.selectedHeight) : 0);
                    setSliderMaxValue(100);
                    setSliderMinValue(0);
                }

                break;
            case SliderType.Stack:
                setSize(selectedStack ? Math.floor(selectedStack.currentRotation) : 0);
                setSliderMinValue(-180);
                setSliderMaxValue(180);
                setStep(45);
                break;
            case SliderType.Trolley:
                setSize(selectedTrolley ? Math.floor(selectedTrolley.currentRotation) : 0);
                setSliderMinValue(-180);
                setSliderMaxValue(180);
                setStep(45);
                break;
            case SliderType.Anesthesia:
                setSize(selectedAnesthesia ? Math.floor(selectedAnesthesia.currentRotation) : 0);
                setSliderMinValue(-180);
                setSliderMaxValue(180);
                setStep(45);
                break;
            case SliderType.Surgeon:
                setSize(selectedSurgeon ? Math.floor(selectedSurgeon.currentRotation) : 0);
                setSliderMinValue(-180);
                setSliderMaxValue(180);
                setStep(45);
                break;
            case SliderType.Console:
                setSize(selectedConsole ? Math.floor(selectedConsole.currentRotation) : 0);
                setSliderMinValue(-180);
                setSliderMaxValue(180);
                setStep(45);
                break;
            case SliderType.Port:
                switch (rotate) {
                    case "X":
                        setSize(selectedPort ? Math.floor(selectedPort.rotateX) : 0);
                        break;
                    case "Y":
                        setSize(selectedPort ? Math.floor(selectedPort.rotateY) : 0);
                }
                setSliderMaxValue(180);
                setSliderMinValue(-180);
                break;
            default:
                setSize(0);
        }
    }, [
        selectedBsu,
        selectedPort,
        selectedStack,
        selectedConsole,
        selectedTrolley,
        selectedSurgeon,
        selectedAnesthesia,
    ]);

    // This function will run when the slider value changed.
    const handleSliderChange = (event: Event, value: number | Array<number>): void => {
        if (!builder.room.selectedObject) return;
        if (builder.room.selectedObject instanceof Robot) {
            const bsu = builder.room.selectedObject as Robot;
            if (typeof value === "number") {
                if (rotate === "Y") {
                    bsu.bsuRotationReminder = value;
                    bsu.setRotationY(value, builder.room);
                    value = bsu.getRotationY();
                } else {
                    bsu.setArmHeight(value);
                }
                setSize(Math.floor(value));
            }
        } else if (builder.room.selectedObject instanceof StaticMesh) {
            const sm = builder.room.selectedObject as StaticMesh;
            if (typeof value === "number") {
                switch (sm.type) {
                    case AssetType.Stack:
                    case AssetType.Console:
                    case AssetType.Trolley:
                    case AssetType.Anesthesia:
                    case AssetType.Surgeon:
                    case AssetType.Slave:
                        sm.setRotationY(value, builder.room);
                        value = sm.getRotationY();
                        break;
                    case AssetType.Trocar:
                        switch (rotate) {
                            case "X":
                                sm.setRotationX(value);
                                break;
                            case "Y":
                                sm.setRotationY(-value);
                                break;
                        }
                }
            }
        }

        if (typeof value === "number") {
            setSize(Math.floor(value));
        }
    };
    return (
        <section className="sliderContainer">
            <h2>{title}</h2>
            <article className="slider">
                <CMRHeightSlider
                    onChange={handleSliderChange}
                    aria-label="Height slider"
                    value={typeof size === "number" ? size : 0}
                    min={sliderMinValue}
                    step={step}
                    max={sliderMaxValue}
                    valueLabelDisplay="on"
                />
            </article>
        </section>
    );
};
CMRSliderComponent.defaultProps = defaultProps;
export default CMRSliderComponent;
